import { inject, Injectable } from '@angular/core';
import {
  SOFTLINE_API_SELF_CHECKOUT_CART,
  SOFTLINE_API_SELF_CHECKOUT_CART_DELETE,
  SOFTLINE_API_SELF_CHECKOUT_CART_FINALIZE,
  SOFTLINE_API_SELF_CHECKOUT_CART_GET,
  SOFTLINE_API_SELF_CHECKOUT_CART_ITEM_DELETE,
  SOFTLINE_API_SELF_CHECKOUT_CART_ITEM_UPDATE,
  SOFTLINE_API_SELF_CHECKOUT_CART_UPDATE,
  SOFTLINE_API_SELF_CHECKOUT_CUSTOMER_CARD_SCAN,
  SOFTLINE_API_SELF_CHECKOUT_ITEMS_SCAN,
} from '@softapps/wws/pos-core';
import { ApiDataAccess } from '@softline/core';
import { CreateCartResponse } from '../types/create-cart-response';
import { Cart, CartItem } from '@softapps/wws/pos-core';
import { Scan } from '@softline/application';
import { Observable } from 'rxjs';
import { SOFTLINE_API_CASH_REGISTER_CART_ITEM } from '@softapps/wws/pos-core';

@Injectable({ providedIn: 'root' })
export class CartRepository {
  readonly #dataAccess = inject(ApiDataAccess);

  async createCart(): Promise<CreateCartResponse> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_SELF_CHECKOUT_CART,
      },
      {}
    );
  }

  async loadCart(cartId: number): Promise<Cart | null> {
    return this.#dataAccess.get({
      path: SOFTLINE_API_SELF_CHECKOUT_CART_GET,
      pathParams: {
        cartId: cartId,
      },
    });
  }

  addScanItem$(
    cartId: number,
    scan: Scan,
    quantity: number,
    forceNewItem = false
  ): Observable<Cart | null> {
    const queryParams = forceNewItem ? { forceNewItem: true } : undefined;
    return this.#dataAccess.create$(
      {
        path: SOFTLINE_API_SELF_CHECKOUT_ITEMS_SCAN,
        pathParams: {
          cartId: cartId,
          type: scan.labelType,
          code: scan.data,
        },
        queryParams,
      },
      {
        quantity: quantity,
      }
    );
  }

  addItem$(
    id: number,
    productId: number,
    quantity = 1,
    forceNewItem?: boolean
  ): Observable<Cart | null> {
    const queryParams = forceNewItem ? { forceNewItem: true } : undefined;
    return this.#dataAccess.create$(
      {
        path: SOFTLINE_API_CASH_REGISTER_CART_ITEM,
        pathParams: { cartId: id },
        queryParams,
      },
      {
        productId: productId,
        quantity: quantity,
      }
    );
  }

  updateItem$(cartId: number, key: string, item: Partial<CartItem>): Observable<Cart> {
    return this.#dataAccess.patch$(
      {
        path: SOFTLINE_API_SELF_CHECKOUT_CART_ITEM_UPDATE,
        pathParams: {
          cartId: cartId,
          key,
        },
      },
      item
    );
  }

  async updateCart(
    id: number,
    changes: Partial<Cart> & { customerCardNumber?: string }
  ): Promise<Cart | null> {
    return await this.#dataAccess.patch(
      {
        path: SOFTLINE_API_SELF_CHECKOUT_CART_UPDATE,
        pathParams: {
          cartId: id,
        },
      },
      changes
    );
  }

  scanCustomerCard$(
    scan: Scan
  ): Observable<{ customerNumber?: number; customerCardNumber?: string } | null> {
    return this.#dataAccess.get$({
      path: SOFTLINE_API_SELF_CHECKOUT_CUSTOMER_CARD_SCAN,
      pathParams: {
        type: scan.labelType,
        code: scan.data,
      },
    });
  }

  async removeCart(cartId: number): Promise<void> {
    await this.#dataAccess.delete({
      path: SOFTLINE_API_SELF_CHECKOUT_CART_DELETE,
      pathParams: { cartId },
    });
  }

  async deleteItem(key: string, cartId: number): Promise<Cart> {
    return await this.#dataAccess.delete({
      path: SOFTLINE_API_SELF_CHECKOUT_CART_ITEM_DELETE,
      pathParams: { key, cartId: cartId },
    });
  }

  async finalize(cartId: number, body: any): Promise<any> {
    return this.#dataAccess.create(
      {
        path: SOFTLINE_API_SELF_CHECKOUT_CART_FINALIZE,
        pathParams: {
          cartId: cartId,
        },
      },
      body
    );
  }
}
